import { GrowthBook } from '@growthbook/growthbook'
import {
  ElektraService,
  Environment,
  HubConfig,
  MySquid,
  ToastHelper,
  localForageHelper,
  onConfigChange,
  onDetectIosApp,
  onEnvironmentChange,
  onFirebaseConfigChange,
} from '@hub/shared'
import { getAnalytics } from 'firebase/analytics'
import { initializeApp } from 'firebase/app'
import { getMessaging, onMessage } from 'firebase/messaging'
import { experimentalSetDeliveryMetricsExportedToBigQueryEnabled } from 'firebase/messaging/sw'
import { getPerformance } from 'firebase/performance'
import { getAppStatus, registerApplication, start } from 'single-spa'
import { constructApplications, constructLayoutEngine, constructRoutes } from 'single-spa-layout'
import { environments as files } from '../node_modules/@hub/shared-dependencies/index'
import changeLayoutAdopt from './assets/js/cookie'
import microfrontendLayout from './microfrontend-layout.html'

const env = ((document.getElementById('environment') as HTMLInputElement)?.value || '')?.trim()
const toast = new ToastHelper().toast
const environments: Environment = files[env || 'local']

startApp()

async function startApp() {
  const gb = new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: 'sdk-ZAlgIWkbzhszyD',
    enableDevMode: true,
    trackingCallback: (experiment, result) => {
      window['dataLayer'].push({
        event: 'experiment_viewed',
        experimentId: experiment.key,
        variationId: result.key,
      })
    },
  })

  await gb.init()

  window['gb'] = gb

  window['environments'] = environments
  onEnvironmentChange.next(environments)
  onDetectIosApp.next((navigator.userAgent.match(/(iPod|iPhone|iPad)/) && window.matchMedia('(display-mode: standalone)').matches) || false)
  const configs: MySquid | null | undefined = await getWebapp()

  const isEmptyMySquid = !configs && !isLocalUrl() && !isTestUrl() && !isFirebaseUrl() && !isProdUrl()
  if (configs?._id || isEmptyMySquid) {
    if (!configs?.active || isEmptyMySquid) {
      window.location.href = 'https://app.wakecreators.com'
    }
    onConfigChange.next(configs)
    await localForageHelper?.setItem('configs', configs)
  } else {
    localForageHelper.removeItem('configs')
  }

  if (environments?.firebase) {
    const app = initializeApp(environments?.firebase)
    const analytics = getAnalytics(app)
    const performance = getPerformance(app)

    let messaging = null
    if (!configs?._id && navigator?.serviceWorker !== undefined) {
      messaging = getMessaging(app)
      experimentalSetDeliveryMetricsExportedToBigQueryEnabled(messaging, true)
      onMessage(messaging, (payload) => {
        console.log('Message received. ', payload)
        const notificationContent = createNotification(payload)
        toast.theme(notificationContent, {
          closeButton: true,
          position: `${window.innerWidth > 991 ? 'bottom center' : 'top center'} `,
          notOverClick: true,
          immediately: true,
          duration: 30000, // 30 seconds
          className: 'notification-in-app',
          onClick: () => {
            history.pushState({}, '', payload?.fcmOptions?.link || '/home')
          },
        })
      })
    }
    onFirebaseConfigChange?.next({ app, analytics, performance, messaging })
  }

  let general: HubConfig = { maintenance: false }
  // this IF is for maintenance mode does not work local
  if (env) {
    general = await getConfig()
  }

  setColors(configs)
  setHeaders(configs, env)
  validateManifest(configs)

  const data: { props; loaders } = {
    props: {
      environments,
      configs,
      general,
    },
    loaders: {},
  }
  const routes = constructRoutes(microfrontendLayout, data)
  const applications = constructApplications({
    routes,
    loadApp({ name }) {
      return System.import(name)
    },
  })
  const layoutEngine = constructLayoutEngine({ routes, applications })
  applications.forEach(registerApplication)
  layoutEngine.activate()
  registerManualMFS(data.props)
  start({
    urlRerouteOnly: false,
  })
  if (!env || env !== 'production') {
    localStorage.setItem('devtools', 'true')
  }
}

function createNotification(payload) {
  return `
    <div class='wrapper-notification-in-app'>
      <header>
        <figure class='icon'>
          <img
            title='${payload?.notification?.title}'
            alt='${payload?.notification?.title}'
            class='img-fluid'
            src='${payload?.notification?.icon || 'https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico'}'
          />
        </figure>
        <div class='text'>
          <h6 class='m-0'>${payload?.notification?.title}</h6>
          <p>${payload?.notification?.body}</p>
        </div>
      </header>
      ${
        payload?.notification?.image && window.innerWidth > 991
          ? `
        <figure class='image'>
          <img
            title='${payload?.notification?.title}'
            alt='${payload?.notification?.title}'
            class='img-fluid'
            src='${payload?.notification?.image || 'https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico'}'
          />
        </figure>
      `
          : ''
      }
    </div>
  `
}

async function getConfig() {
  try {
    return fetch(
      `https://storage.googleapis.com/web-hub/configs/config.${env && env !== 'local' && !env.includes('aws') ? `${env}.` : ''}json`
    ).then((r) => r.json())
  } catch (error) {
    return { maintenance: false }
  }
}

async function getWebapp() {
  try {
    const elektraService = new ElektraService()
    if (!isProdUrl()) {
      return await elektraService?.getConfigByHeader()
    }
    return null
  } catch (error) {
    return null
  }
}

function registerManualMFS(props: any) {
  const thisWindow: any = window
  const toRegisterList = [
    // {
    //   name: 'profile',
    //   activeWhen: ['/profile'],
    //   windowName: '@hub/profile',
    // },
    // {
    //   name: 'campaigns',
    //   activeWhen: ['/campaigns', '/dashboard'],
    //   windowName: '@hub/campaigns',
    // },
    // {
    //   name: 'payments',
    //   activeWhen: ['/payments', '/payment'],
    //   windowName: '@hub/payments',
    // },
    // {
    //   name: 'mediakit',
    //   activeWhen: ['/mediakit'],
    //   windowName: '@hub/mediakit',
    // },
  ]

  toRegisterList.forEach((item) => {
    const REGISTRED = getAppStatus(`@hub/${item.name}`)
    if (!REGISTRED && thisWindow[item.windowName]) {
      registerApplication({
        name: `@hub/${item.name}`,
        app: thisWindow[item.windowName],
        activeWhen: (location) => {
          return item.activeWhen.some((path) => location.pathname.startsWith(path))
        },
        customProps: {
          ...props,
        },
      })
    }
  })
}

function isProdUrl() {
  return [
    'https://app.squidit.com.br',
    'https://hub.squidit.com.br',
    'https://firebase-hub.squidit.com.br',
    // Novos domínios
    'https://app.wakecreators.com',
    'https://hub.wakecreators.com',
    'https://firebase-hub.wakecreators.com',
  ].includes(window.location.origin)
}

function isTestUrl() {
  return [
    'https://stg-app.squidit.com.br',
    'https://stg-hub.squidit.com.br',
    'https://dev-app.squidit.com.br',
    'https://dev-hub.squidit.com.br',
    'https://stg-firebase-hub.squidit.com.br',
    'https://dev-firebase-hub.squidit.com.br',
    // Novos domínios
    'https://stg-app.wakecreators.com',
    'https://stg-hub.wakecreators.com',
    'https://dev-app.wakecreators.com',
    'https://dev-hub.wakecreators.com',
    'https://stg-firebase-hub.wakecreators.com',
    'https://dev-firebase-hub.wakecreators.com',
  ].includes(window.location.origin)
}

function isFirebaseUrl() {
  return window.location.origin.includes('firebaseapp') || window.location.origin.includes('web.app')
}

function isLocalUrl() {
  return window.location.origin.includes('localhost')
}

function validateManifest(configs: MySquid) {
  if (configs?._id) {
    const manifest = document.getElementById('manifest')
    if (manifest) {
      manifest.remove()
    }
  }
}

function setColors(configs?: MySquid | undefined) {
  let variables = ''
  if (configs?._id) {
    const createInCss = window['createInCss']
    const colors = createInCss('primary', configs?.theme?.primaryColor)
    const lightColors = `
      --primary_color: var(--primary-40) !important;
      --primary_color_hover: var(--primary-50) !important;
    `
    const darkColors = `
      --primary_color: var(--primary-70) !important;
      --primary_color_hover: var(--primary-60) !important;
    `
    variables = `
      html {
        --secondary_color: ${configs?.theme?.secondaryColor} !important;
        --tertiary_color: ${configs?.theme?.textColor} !important;
        ${lightColors}
      }
      @media (prefers-color-scheme: dark) {
        html {
          ${darkColors}
        }
      }
      html.light {
        ${lightColors}
      }
      html.dark {
        ${darkColors}
      }
    `
  }
  const html = document.getElementsByTagName('html')[0]
  if (configs?.theme?.theme && configs?.theme?.theme !== 'both') {
    html.classList.add(configs.theme.theme)
  } else if (window?.matchMedia && window?.matchMedia('(prefers-color-scheme: dark)')?.matches) {
    html.classList.add('dark')
  } else {
    html.classList.add('light')
  }
  if (variables) {
    const head = document.getElementsByTagName('head')[0]
    const style = document.createElement('style')
    style.appendChild(document.createTextNode(variables))
    head.appendChild(style)
  }
}

function setHeaders(configs: MySquid | undefined, env: string) {
  let metas = `
    <title>Wake Creators Hub</title>
    <meta name='apple-mobile-web-app-title' content='Wake Creators Hub' />
    <meta name='application-name' content='Wake Creators Hub' />
    <meta name='description' content='A Wake Creators é uma empresa de tecnologia, com mais de 10 anos de existência, líder em marketing de influência baseado em dados.' />
    <meta name='msapplication-TileColor' content='#C505F2' />
    <meta name='theme-color' content='#C505F2' />
    <meta name="apple-mobile-web-app-status-bar" content="#C505F2" />
    <link rel='shortcut icon' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico' />
    <link rel='icon' type='image/png' sizes='16x16' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' />
    <link rel='icon' type='image/png' sizes='32x32' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' />
    <link rel='icon' type='image/png' sizes='194x194' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' />
    <link rel='apple-touch-icon' sizes='180x180' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' />
    <link rel='mask-icon' href='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' color='#C505F2' />
    <meta name='msapplication-TileImage' content='https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico?new=fav3' />
    <link rel="icon" type="image/png" href="https://cdn.jsdelivr.net/gh/squidit/css/favicon.ico" />
  `
  if (configs?._id) {
    metas = `
      <title>${configs?.metadata?.pageTitle}</title>
      <meta name='apple-mobile-web-app-title' content=${configs?.metadata?.pageTitle} />
      <meta name='application-name' content=${configs?.metadata?.pageTitle} />
      <meta name='description' content=${configs?.metadata?.pageDescription || ''} />
      <meta name='msapplication-TileColor' content="${configs?.theme?.primaryColor}" />
      <meta name="apple-mobile-web-app-status-bar" content="${configs?.theme?.primaryColor}" />
      <meta name='theme-color' content=${configs?.theme?.primaryColor} />
      <link rel='shortcut icon' href=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel='icon' type='image/png' sizes='16x16' href=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel='icon' type='image/png' sizes='32x32' href=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel='icon' type='image/png' sizes='194x194' href=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel='apple-touch-icon' sizes='180x180' href=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel='mask-icon' href=${configs?.images?.favicon?.url}?new=fav3 color=${configs?.theme?.primaryColor} />
      <meta name='msapplication-TileImage' content=${configs?.images?.favicon?.url}?new=fav3 />
      <link rel="icon" type="image/png" href=${configs?.images?.favicon?.url} />
    `
  }

  if (env !== 'production') {
    metas += `
      <meta name='robots' content='noindex' />
    `
  }

  // ? Essa linha sobrepõe os estilos do adopt, por isso é necessário executar o changeLayoutAdopt após a sua execução
  document.head.innerHTML += metas

  if (changeLayoutAdopt && typeof changeLayoutAdopt === 'function') {
    changeLayoutAdopt()
  }
}
